import { useState } from 'react';
import { describeOffer } from '@affily/shared/commission';
import { PROGRAM_TEMPLATES, BUSINESS_TYPES, getTemplate } from '@affily/shared/templates';

/**
 * "What kind of business is it?", then "What are you selling?" — the program
 * templates as a grid of buttons, two to a row on a phone and up to four on a
 * desktop. Picking a business type picks its best template and narrows the
 * grid to what suits it, best first; "Show all" (or "Something else") brings
 * back every template. The chosen one's blurb shows underneath, which keeps
 * the buttons short enough to sit side by side.
 *
 * `value` is a template key, or null for starting from scratch; `onChange`
 * is called with the same.
 */
export function TemplateSelect({ value, onChange, scratchBlurb, className = '' }) {
  // Not guessed from `value`: a form opens on a default template the brand never chose.
  const [type, setType] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const chosen = getTemplate(value);
  const business = BUSINESS_TYPES.find((t) => t.key === type);

  const templates = business && !showAll ? business.templates.map(getTemplate) : PROGRAM_TEMPLATES;
  const options = [
    ...templates.map((t) => ({
      key: t.key,
      name: t.name,
      offer: describeOffer(t.payload, t.payload.gifts),
    })),
    { key: null, name: 'Something else — start from scratch', offer: null },
  ];

  function pickType(key) {
    setType(key);
    setShowAll(false);
    const first = BUSINESS_TYPES.find((t) => t.key === key)?.templates[0];
    if (first) onChange(first);
  }

  return (
    <div className={className}>
      <div role="group" aria-labelledby="business-label">
        <p id="business-label" className="mb-1.5 text-sm font-medium text-ink">
          What kind of business is it?
        </p>
        <div className="mb-5 flex flex-wrap gap-1.5">
          {[...BUSINESS_TYPES, { key: 'other', name: 'Something else' }].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => pickType(t.key)}
              aria-pressed={type === t.key}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                type === t.key
                  ? 'border-accent bg-accent text-white'
                  : 'border-rule-strong bg-paper-raised text-ink-muted hover:text-ink'
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      <div role="group" aria-labelledby="template-label">
        <p id="template-label" className="mb-1.5 text-sm font-medium text-ink">
          {business && !showAll ? 'Suggested for this kind of business' : 'What are you selling?'}
        </p>
        <p className="mb-3 text-xs text-ink-muted">
          Pick the closest fit. The terms are only suggestions; you can change all of them.
          {business && !showAll && (
            <>
              {' '}
              <button type="button" className="text-accent hover:underline" onClick={() => setShowAll(true)}>
                Show all templates
              </button>
            </>
          )}
        </p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {options.map((o, i) => {
            const active = (value ?? null) === o.key;
            return (
              <button
                key={o.key ?? 'scratch'}
                type="button"
                onClick={() => onChange(o.key)}
                aria-pressed={active}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? 'border-accent bg-accent-soft'
                    : o.key
                      ? 'border-rule-strong bg-paper-raised hover:bg-paper-sunken'
                      : 'border-dashed border-rule-strong hover:bg-paper-sunken'
                }`}
              >
                <span className="block text-sm font-medium">{o.name}</span>
                {o.offer && (
                  <span className="mt-0.5 block text-xs font-medium tabular-nums text-accent">
                    e.g. {o.offer}
                  </span>
                )}
                {business && !showAll && i === 0 && (
                  <span className="mt-1 block text-2xs font-medium text-positive">Best fit</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-sm text-ink-muted">{chosen ? chosen.blurb : scratchBlurb}</p>
      </div>
    </div>
  );
}
